import { Injectable, inject } from '@angular/core';
import { AWSLambda } from '../../AWSLambda.service';
import { ActivatedRoute, Router } from '@angular/router';

/**
 * Servicio que gestiona la lógica relacionada con promociones,
 * como validar códigos promocionales y verificar si hay promociones activas para un producto.
 */
@Injectable({
  providedIn: 'root'
})
export class PromoService {

  /**
   * Instancia del servicio AWSLambda, inyectado mediante la API de `inject()`.
   */
  lambda = inject(AWSLambda);

  /**
   * Crea una instancia de `PromoService`.
   *
   * @param route Proporciona acceso a la ruta activa.
   * @param router Permite acceder a la URL actual para extraer el producto.
   */
  constructor(private route: ActivatedRoute, private router: Router) { }

  /**
   * Verifica si hay promociones activas para el producto actual (derivado de la URL).
   *
   * @param product_old (No se usa realmente en la lógica, pero se mantiene por compatibilidad).
   * @returns Una promesa que resuelve en `true` si hay promociones activas, o `false` en caso contrario.
   */
  async isPromosActive(product_old: string): Promise<boolean> {
    const product = this.router.url
      .split('?')[0]
      .split('#')[0]
      .split('/')
      .filter(Boolean)[0]?.split('-')[0] || '';

    console.log('Validando promo activa para:', product);

    try {
      const promo_active = await this.lambda.getLambdaFunction(
        'lambda',
        '/promos',
        {
          queryStringParameters: {product: product}
        }
      );

      return promo_active.promo_active;
    } catch (error) {
      console.error('isPromosActive:', error);
      return false;
    }
  }

  /**
   * Valida un código promocional para el producto actual (basado en la URL).
   *
   * @param code El código promocional introducido por el usuario.
   * @returns Una promesa que resuelve con un objeto que contiene un array `promos` con los datos de la promoción.
   */
  async validatePromoCode(code: string): Promise<{ promos: Promo[] }> {
    const product = this.router.url
      .split('?')[0]
      .split('#')[0]
      .split('/')
      .filter(Boolean)[0]?.split('-')[0] || '';

    console.log('Validando código:', code, ' para este producto:', product);

    try {
      const promo_data = await this.lambda.getLambdaFunction(
        'lambda',
        '/promos',
        {
          queryStringParameters: {
            method: 'validatePromo',
            promo_code: code,
            applyesTo: product
          }
        }
      );

      return promo_data;
    } catch (error) {
      console.error('validatePromoCode:', error);
      return { promos: [] };
    }
  }
}

export interface Promo {
  id: string;
  code: string;
  quantity: number;
  [key: string]: any;
}
